import { useState, useEffect, useCallback } from 'react';
import { API, Storage, graphqlOperation } from 'aws-amplify';
import { listDealFilesByDealIDType } from '../../graphql/queries';
import DealRepository from '../../application/repository/DealRepository';
import getUserAvatar from '../../application/common/utils/getUserAvatar';

export interface DealComment {
  id: string;
  comment: string;
  contactType?: string;
  createdAt: string;
  updatedAt: string;
  dispositionTitle?: string;
  user: {
    id: string;
    name: string;
    initials: string;
    profilePicture?: string;
  };
}

export interface DealAppointment {
  id?: string;
  appointmentDate: string;
  appointmentStartTime: number;
  appointmentEndTime?: number;
  status: string;
  notes?: string;
  deal?: { name?: string };
  user?: { name?: string };
  setter?: { name?: string };
}

export interface DealPhoto {
  id: string;
  imageName: string;
  tags: string[];
  description: string;
  sequence: number;
  createdAt: string | null;
  type?: string;
  url?: string;
}

export interface DealAttribute {
  label: string;
  val: string | number | Record<string, any>;
}

export interface DealMetaData {
  id: string;
  extID: string;
  createdAt: string;
  createdBy?: {
    name: string;
    email: string;
    id: string;
    extID: string;
  };
  updatedAt: string;
  updatedBy?: {
    name: string;
    email: string;
    id: string;
    extID: string;
  };
}

export interface DealDetailFull {
  id: string;
  name: string;
  stage: string;
  stageId: string;
  categoryId: string;
  updatedAt: string;
  currentOwnerID: string;
  setter?: {
    id: string;
    name: string;
    initials: string;
    profilePicture?: string;
  };
  closer?: {
    id: string;
    name: string;
    initials: string;
    profilePicture?: string;
  } | null;
  mainDisplayAttrs: DealAttribute[];
  comments: DealComment[];
  appointments: DealAppointment[];
  photos: DealPhoto[];
  metaData: DealMetaData;
  rawDeal: any; // full deal object for passing to existing components
}

interface UseDealDetailReturn {
  dealDetail: DealDetailFull | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useDealDetail(dealId: string | null): UseDealDetailReturn {
  const [dealDetail, setDealDetail] = useState<DealDetailFull | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchDeal = useCallback(async () => {
    if (!dealId) {
      setDealDetail(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await DealRepository.fetchDeal(dealId, null);

      if (!data) {
        setDealDetail(null);
        setIsLoading(false);
        return;
      }

      const deal = data.deal;
      const mainDisplay = data.mainDisplay || {};
      const comments: DealComment[] = (data.comments || []).map((c: any) => ({
        id: c.id,
        comment: c.comment,
        contactType: c.contactType,
        createdAt: c.createdAt,
        updatedAt: c.updatedAt,
        dispositionTitle: c.disposition?.title,
        user: {
          id: c.user?.id || '',
          name: c.user?.name || 'Unknown',
          initials: c.user?.initials || '?',
          profilePicture: c.user?.profilePicture,
        },
      }));

      const appointments: DealAppointment[] = (data.appointments || []).map(
        (a: any) => ({
          id: a.id,
          appointmentDate: a.appointmentDate,
          appointmentStartTime: a.appointmentStartTime,
          appointmentEndTime: a.appointmentEndTime,
          status: a.status || 'scheduled',
          notes: a.notes,
          deal: a.deal,
          user: a.user,
          setter: a.setter,
        })
      );

      // Process photos from deal.files.items and resolve signed URLs.
      //
      // The embedded files connection returns AT MOST one AppSync page
      // (~100 rows, no nextToken plumbed through the deal query) — a deal
      // with 500 inspection photos silently showed ~99 on the web: wrong
      // grid, wrong Download All, no way to see the rest (found live
      // 2026-08-18, 506-photo AOE Restoration deal). When the embedded page
      // is suspiciously full, re-fetch the COMPLETE file list through the
      // paginated byDealID query instead.
      let fileItems: any[] = Array.isArray(deal?.files?.items)
        ? deal.files.items
        : [];
      if (fileItems.length >= 100) {
        try {
          const all: any[] = [];
          let nextToken: string | null = null;
          do {
            const resp: any = await API.graphql(
              graphqlOperation(listDealFilesByDealIDType, {
                dealID: dealId,
                limit: 500,
                nextToken,
              })
            );
            const page = resp?.data?.listDealFilesByDealIDType;
            all.push(...(page?.items || []));
            nextToken = page?.nextToken || null;
          } while (nextToken);
          if (all.length >= fileItems.length) {
            fileItems = all;
          }
        } catch (e) {
          console.warn(
            'Full deal-file pagination failed; falling back to embedded page',
            e
          );
        }
      }

      const rawPhotos = fileItems.filter(
        (p: any) =>
          p &&
          !p.isDeleted &&
          // Measurement/estimate report documents are deal FILES, not
          // photos — they show on the Claim tab, not the photo grid
          // (GG-3433 D1).
          !['measurement_report', 'estimate'].includes(p.category)
      );

      const photos: DealPhoto[] = await Promise.all(
        rawPhotos.map(async (p: any, idx: number) => {
          const imageKey = p.url || p.id || p.fileName || '';
          let resolvedUrl = '';

          if (imageKey) {
            try {
              resolvedUrl = (await Storage.get(imageKey)) as string;
            } catch (e) {
              console.warn('Failed to resolve photo URL:', imageKey);
            }
          }

          return {
            id: p.id || `photo-${idx}`,
            imageName: imageKey,
            tags: p.tags || [],
            description: p.description || '',
            sequence: p.sequence || idx,
            createdAt: p.createdAt || null,
            type: p.type || 'image',
            url: resolvedUrl,
          };
        })
      );

      // Also include photos from flexAttributes.photo that aren't already in deal.files.items
      try {
        const flexAttrs = deal?.flexAttributes
          ? typeof deal.flexAttributes === 'string'
            ? JSON.parse(deal.flexAttributes)
            : deal.flexAttributes
          : {};
        const flexPhotoArray: string[] = Array.isArray(flexAttrs.photo)
          ? flexAttrs.photo
          : [];
        const existingKeys = new Set(photos.map((p) => p.imageName));

        const validFlexPhotos = flexPhotoArray.filter(
          (imageName) =>
            imageName &&
            imageName !== 'undefined' &&
            imageName !== 'null' &&
            !imageName.includes('undefined') &&
            !existingKeys.has(imageName)
        );

        const resolvedFlexPhotos = await Promise.all(
          validFlexPhotos.map(async (imageName, idx) => {
            let resolvedUrl = '';
            try {
              resolvedUrl = (await Storage.get(imageName)) as string;
            } catch (e) {
              console.warn('Failed to resolve flex photo URL:', imageName);
            }

            // Detect file type from extension
            const ext = imageName.split('.').pop()?.toLowerCase() || '';
            let fileType = 'image/jpeg';
            if (ext === 'pdf') {
              fileType = 'application/pdf';
            } else if (ext === 'png') {
              fileType = 'image/png';
            } else if (ext === 'gif') {
              fileType = 'image/gif';
            } else if (['mp4', 'mov', 'avi', 'mkv'].includes(ext)) {
              fileType = `video/${ext === 'mov' ? 'quicktime' : ext}`;
            }

            return {
              id: `flex-photo-${idx}`,
              imageName,
              tags: [] as string[],
              description: fileType === 'application/pdf' ? 'PDF Document' : '',
              sequence: rawPhotos.length + idx,
              createdAt: null,
              type: fileType,
              url: resolvedUrl,
            };
          })
        );

        photos.push(...resolvedFlexPhotos);
      } catch (e) {
        console.warn('Error processing flexAttributes photos:', e);
      }

      // Get setter/closer info - use the already-resolved data from DealRepository
      // DealRepository.fetchDeal already processes ownerHistory and falls back to deal.user
      let setter;
      let closer = null;

      if (deal.setter?.user) {
        setter = {
          id: deal.setter.user.id,
          name: deal.setter.user.name,
          initials: deal.setter.user.initials || '',
          profilePicture: deal.setter.profilePicture || '',
        };
      } else if (deal.user) {
        // Fallback: if repository didn't set setter, use deal.user
        const profilePicture = await getUserAvatar(
          deal.user.imageName,
          '',
          true
        );
        setter = {
          id: deal.user.id,
          name: deal.user.name,
          initials: deal.user.initials || '',
          profilePicture,
        };
      }

      if (deal.closer?.user) {
        closer = {
          id: deal.closer.user.id,
          name: deal.closer.user.name,
          initials: deal.closer.user.initials || '',
          profilePicture: deal.closer.profilePicture || '',
        };
      }

      const metaData: DealMetaData = data.dealMetaData || {
        id: deal?.id || '',
        extID: deal?.extID || '',
        createdAt: deal?.createdAt || '',
        updatedAt: deal?.updatedAt || '',
      };

      const result: DealDetailFull = {
        id: deal.id,
        name: deal.name,
        stage: mainDisplay.dealStage || deal?.stage?.title || '',
        stageId: deal.currentStageID || '',
        categoryId: deal.categoryID || '',
        updatedAt: deal.updatedAt || '',
        currentOwnerID: deal.currentOwnerID || '',
        setter,
        closer,
        mainDisplayAttrs: mainDisplay.mainDisplayAttrs || [],
        comments,
        appointments,
        photos,
        metaData,
        rawDeal: deal,
      };

      setDealDetail(result);
    } catch (err) {
      console.error('Error fetching deal detail:', err);
      setError(
        err instanceof Error ? err : new Error('Failed to fetch deal detail')
      );
    } finally {
      setIsLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    fetchDeal();
  }, [fetchDeal]);

  return {
    dealDetail,
    isLoading,
    error,
    refetch: fetchDeal,
  };
}
